
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { CheckCircle2, MessageSquare, Car, Receipt, Gauge, Shield, MapPin, ChevronRight, Menu, X, ArrowUp } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import LogoProcessor from "@/components/LogoProcessor";
import Footer from "@/components/Footer";
import Affiliations from "@/components/Affiliations";
import Reveal from "@/components/Reveal";
import DataControl from "@/components/DataControl";


/**
 * COUNTEDCARE LANDING MOCKUP
 * -------------------------------------------------
 * Drop this component in your Lovable project and route it to "/".
 * Place the five screenshot images in /public/images/ with these exact names:
 *   - preview-modal.png        (Add expense modal with scan/mileage/manual)
 *   - preview-chat.png         (CareAI Assistant chat)
 *   - preview-dashboard.png    (Home dashboard w/ Tax Savings Progress)
 *   - preview-expenses.png     (Expenses list w/ categories)
 *   - preview-resources.png    (Resources results list)
 */

const screenshots = [
  { src: "/images/preview-dashboard.png", alt: "Dashboard with tax progress", caption: "Track progress toward the IRS 7.5% AGI threshold" },
];

function PhoneFrame({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="relative w-[350px] h-[700px] md:w-[400px] md:h-[800px] shrink-0 mx-auto">
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-contain"
        onError={(e) => {
          e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjY2MCIgdmlld0JveD0iMCAwIDMyMCA2NjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iNjYwIiBmaWxsPSIjMTExODI3Ii8+Cjx0ZXh0IHg9IjE2MCIgeT0iMzMwIiBmaWxsPSIjNjM2NmYxIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LWZhbWlseT0ic3lzdGVtLXVpIiBmb250LXNpemU9IjE0Ij5Db3VudGVkQ2FyZSBQcmV2aWV3PC90ZXh0Pgo8L3N2Zz4K';
        }}
      />
    </div>
  );
}

export default function Index() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
      
      // Update active section for navigation
      const sections = ["features", "why", "how"];
      const currentSection = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      setActiveSection(currentSection || "");
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    
    // Check if it's a hash link (for scrolling) or a route (for navigation)
    if (href.startsWith("#")) {
      const element = document.getElementById(href.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      // Navigate to the route
      navigate(href);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-white scroll-smooth">
      {/* NAV */}
      <header
        className={`sticky top-0 z-50 transition-all duration-500 ease-out border-b ${
          showScrollTop
            ? "bg-white/90 supports-[backdrop-filter]:bg-white/70 backdrop-blur-xl border-slate-200 shadow-[0_1px_24px_-12px_rgba(15,23,42,0.35)]"
            : "bg-white/95 supports-[backdrop-filter]:bg-white/80 backdrop-blur-xl border-slate-200/70"
        }`}
      >
        <div className={`mx-auto max-w-7xl px-4 sm:px-6 flex items-center justify-between gap-6 transition-all duration-500 ease-out ${showScrollTop ? "py-2.5" : "py-4"}`}>
          <button 
            onClick={() => handleNavClick("#")}
            className="flex items-center gap-2 rounded-lg transition-opacity duration-300 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2"
            aria-label="CountedCare Home"
          >
            <LogoProcessor />
          </button>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1 text-[0.9375rem] font-medium text-slate-600">
            {[
              { href: "#features", label: "Features" },
              { href: "#why", label: "Why Track" },
              { href: "#how", label: "How it works" },
              { href: "/about", label: "About" },
            ].map(({ href, label }) => {
              const isActive = href.startsWith("#") && activeSection === href.substring(1);
              return (
                <button
                  key={href}
                  onClick={() => handleNavClick(href)}
                  aria-current={isActive ? "true" : undefined}
                  className={`relative rounded-lg px-3 py-2 transition-colors duration-300 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2 after:absolute after:left-3 after:right-3 after:-bottom-0.5 after:h-[2px] after:rounded-full after:bg-sky-600 after:transition-transform after:duration-500 after:ease-out after:origin-left ${
                    isActive ? "text-slate-900 after:scale-x-100" : "after:scale-x-0 hover:after:scale-x-100"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handleNavClick("/contact")}
              className="hidden lg:inline-flex items-center rounded-xl px-3.5 py-2 text-[0.9375rem] font-medium text-slate-600 transition-colors duration-300 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2"
            >
              Contact
            </button>
            <Button 
              className="hidden md:inline-flex rounded-xl bg-sky-700 px-5 text-white shadow-[0_8px_20px_-10px_rgba(3,105,161,0.9)] transition-all duration-300 hover:bg-sky-800 hover:shadow-[0_12px_26px_-10px_rgba(3,105,161,0.85)]"
              onClick={() => window.open('https://app.countedcare.com/', '_blank')}
            >
              Start Tracking
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200/60 bg-white/95 backdrop-blur">
            <nav className="px-4 py-4 space-y-1">
              {[
                { href: "#features", label: "Features" },
                { href: "#why", label: "Why Track" },
                { href: "#how", label: "How it works" },
                { href: "/about", label: "About" },
                { href: "/contact", label: "Contact" }
              ].map(({ href, label }) => (
                <button
                key={href}
                  onClick={() => handleNavClick(href)}
                  className="block w-full text-left px-3 py-2.5 font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors duration-300"
                >
                  {label}
                </button>
              ))}
              <div className="pt-4 mt-3 border-t border-slate-200 space-y-3">
                <Button 
                  className="w-full rounded-xl bg-sky-700 hover:bg-sky-800 transition-all duration-300"
                  onClick={() => window.open('https://app.countedcare.com/', '_blank')}
                >
                  Start Tracking
                </Button>
                <div className="flex items-center justify-center gap-3">
                  <a href="https://apps.apple.com/us/app/countedcare/id6757411837" target="_blank" rel="noopener noreferrer">
                    <img src="/images/app-store-badge.svg" alt="Download on the App Store" className="h-10" />
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.countedcare.countedcare&hl=en" target="_blank" rel="noopener noreferrer">
                    <img src="/images/google-play-badge.png" alt="Get it on Google Play" className="h-10" />
                  </a>
                </div>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* HERO */}
<section className="relative overflow-hidden animate-fade-in">
  <div className="mx-auto max-w-7xl px-4 pt-2 pb-4 grid lg:grid-cols-2 gap-2 items-center">
    {/* LEFT SIDE CONTENT */}
    <div className="animate-fade-in">
      <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs bg-white hover-scale transition-all duration-200">
        <span className="h-2 w-2 rounded-full bg-emerald-500" /> Beta
      </div>
      <h1 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-slate-900">
        Track caregiving costs. <span className="text-sky-700">Unlock tax savings.</span>
      </h1>
      <p className="mt-4 text-slate-600 text-lg">
        CountedCare helps family caregivers capture receipts, mileage, and medical expenses — then maps them to IRS Publication 502 so you can see progress toward the 7.5% AGI threshold.
      </p>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <a href="https://apps.apple.com/us/app/countedcare/id6757411837" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity duration-200">
          <img src="/images/app-store-badge.svg" alt="Download on the App Store" className="h-11" />
        </a>
        <a href="https://play.google.com/store/apps/details?id=com.countedcare.countedcare&hl=en" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity duration-200">
          <img src="/images/google-play-badge.png" alt="Get it on Google Play" className="h-11" />
        </a>
      </div>
      <p className="mt-4 text-xs text-slate-500 max-w-prose">
        * CountedCare provides educational guidance and tools. We are not a tax advisor. Please consult a qualified professional for personalized advice.
      </p>

      {/* trust row */}
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-slate-500">
        <div className="flex items-center gap-2"><Shield className="h-4 w-4"/>Secure by design</div>
        <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4"/>Built for caregivers</div>
        <div className="flex items-center gap-2"><Gauge className="h-4 w-4"/>7.5% AGI tracker</div>
        <div className="flex items-center gap-2"><Receipt className="h-4 w-4"/>Receipt OCR</div>
      </div>
    </div>

    {/* RIGHT SIDE PHONE PREVIEW */}
    <div className="animate-fade-in flex items-center justify-center lg:justify-end lg:-mt-16">
      <div className="transition-transform duration-700 ease-out hover:-translate-y-1">
        <PhoneFrame src={screenshots[0].src} alt={screenshots[0].alt} />
      </div>
    </div>
  </div>
</section>

      <Affiliations />

      {/* FEATURE CARDS */}
      <section id="features" className="mx-auto max-w-7xl px-4 py-12">
        <Reveal className="max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">Everything you need to keep caregiving costs organized</h2>
          <p className="mt-2 text-slate-600">Designed for real life: quick capture, smart categorization, and clear progress toward potential deductions.</p>
        </Reveal>
        <Reveal className="stagger-reveal mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="hover-scale transition-all duration-200 hover:shadow-lg">
            <CardHeader className="pb-2 flex items-center gap-2">
              <Receipt className="h-5 w-5 text-sky-700" />
              <CardTitle className="text-base">Scan / Upload Receipts</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-slate-600">Snap a photo or upload; we pull date, total, and vendor and map to Pub 502 categories.</CardContent>
          </Card>
          <Card className="hover-scale transition-all duration-200 hover:shadow-lg">
            <CardHeader className="pb-2 flex items-center gap-2">
              <Car className="h-5 w-5 text-sky-700" />
              <CardTitle className="text-base">Mileage Tracker</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-slate-600">Enter start/end or use GPS. We apply the current IRS medical mileage rate automatically.</CardContent>
          </Card>
          <Card className="hover-scale transition-all duration-200 hover:shadow-lg">
            <CardHeader className="pb-2 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-sky-700" />
              <CardTitle className="text-base">CareAI Assistant</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-slate-600">Ask questions about qualified expenses, documentation, or how to stay audit‑ready.</CardContent>
          </Card>
          <Card className="hover-scale transition-all duration-200 hover:shadow-lg">
            <CardHeader className="pb-2 flex items-center gap-2">
              <Gauge className="h-5 w-5 text-sky-700" />
              <CardTitle className="text-base">Tax Savings Progress</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-slate-600">See your total eligible spend and progress toward the 7.5% AGI threshold in real time.</CardContent>
          </Card>
          <Card className="hover-scale transition-all duration-200 hover:shadow-lg">
            <CardHeader className="pb-2 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-sky-700" />
              <CardTitle className="text-base">Local Resources</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-slate-600">Discover transportation assistance, respite care, and county programs near you.</CardContent>
          </Card>
          <Card className="hover-scale transition-all duration-200 hover:shadow-lg">
            <CardHeader className="pb-2 flex items-center gap-2">
              <Shield className="h-5 w-5 text-sky-700" />
              <CardTitle className="text-base">Private & Secure</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-slate-600">SOC‑2 minded practices, least‑privilege access, and encrypted storage.
            </CardContent>
          </Card>
        </Reveal>
      </section>

      {/* WHY TRACK EXPENSES */}
      <section id="why" className="mx-auto max-w-7xl px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* IMAGE */}
          <Reveal delay={100} className="order-1 lg:order-2">
            <div className="relative overflow-hidden rounded-2xl border bg-white shadow-sm">
              <img
                src="/images/caregiver.jpg"
                alt="Caregiver supporting their loved one at home"
                className="w-full h-[340px] md:h-[460px] object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          </Reveal>

          {/* COPY */}
          <Reveal className="order-2 lg:order-1">
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
              Why track medical & dental expenses
            </h2>
            <p className="mt-3 text-slate-600 text-lg">
              Family caregivers spend about <span className="font-semibold text-sky-700">26% of their income</span> out-of-pocket on caregiving costs, and <span className="font-semibold text-sky-700">billions of dollars</span> in medical expense deductions go unclaimed every year. Tracking those expenses helps you stay organized and uncover savings opportunities.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              Sources:{" "}
              <a href="https://www.aarp.org/caregiving/financial-legal/info-2021/family-caregivers-cost-survey.html" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-700">AARP Family Caregiving Out-of-Pocket Costs Study</a>
              {" · "}
              <a href="https://www.tiaa.org/public/institute/publication/2024/subsidizing-medical-spending-through-the-tax-code-take-up-and-targeting" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-700">TIAA Institute (2024)</a>
            </p>

            <ul className="mt-5 space-y-3 text-slate-700">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 mt-0.5 text-sky-700" />
                <span>See progress toward the IRS <strong>7.5% AGI</strong> threshold for potential deductions.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 mt-0.5 text-sky-700" />
                <span>Keep receipts, mileage, and medical payments organized in one place.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 mt-0.5 text-sky-700" />
                <span>Export clean summaries for your tax professional when you're ready.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 mt-0.5 text-sky-700" />
                <span>Reduce stress and know what's deductible vs. what's not.</span>
              </li>
            </ul>

            <div className="mt-6 flex flex-wrap gap-3">
              <Button 
                className="bg-sky-600 hover:bg-sky-700 hover-scale transition-all duration-200 focus:ring-2 focus:ring-sky-600 focus:ring-offset-2"
                onClick={() => window.open('https://app.countedcare.com/', '_blank')}
              >
                Start Tracking
              </Button>
              <Button 
                variant="outline" 
                className="hover-scale transition-all duration-200 focus:ring-2 focus:ring-sky-600 focus:ring-offset-2"
                onClick={() => window.open('https://www.irs.gov/pub/irs-pdf/p502.pdf', '_blank')}
              >
                See What Qualifies
              </Button>
            </div>

            <p className="mt-4 text-xs text-slate-500">
              * Educational information only; not tax advice. Please consult a qualified professional for personalized guidance.
            </p>
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-sky-50/60 border-y border-sky-100">
        <div className="mx-auto max-w-7xl px-4 py-12">
          <Reveal as="h2" className="text-2xl md:text-3xl font-semibold text-slate-900">How it works</Reveal>
          <Reveal className="stagger-reveal mt-6 grid md:grid-cols-3 gap-4">
            <Card className="hover-scale transition-all duration-200 hover:shadow-lg">
              <CardHeader>
                <CardTitle className="text-base">1. Create your profile</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600">Enter caregiver details, set your AGI estimate, and optionally connect accounts for faster categorization.</CardContent>
            </Card>
            <Card className="hover-scale transition-all duration-200 hover:shadow-lg">
              <CardHeader>
                <CardTitle className="text-base">2. Track expenses your way</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600">Scan receipts, log mileage, or add manual entries. We tag items to IRS Pub 502 and flag what looks deductible.</CardContent>
            </Card>
            <Card className="hover-scale transition-all duration-200 hover:shadow-lg">
              <CardHeader>
                <CardTitle className="text-base">3. See savings & export</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600">Watch your progress bar grow. Export summaries for your tax professional when you're ready.</CardContent>
            </Card>
          </Reveal>
          <Reveal delay={150} className="mt-6">
            <Button 
              className="bg-sky-600 hover:bg-sky-700 hover-scale transition-all duration-200 focus:ring-2 focus:ring-sky-600 focus:ring-offset-2"
              onClick={() => window.open('https://app.countedcare.com/', '_blank')}
            >
              Start Tracking
            </Button>
          </Reveal>
        </div>
      </section>

      <DataControl />

      {/* WHO WE ARE */}
      <section id="who" className="bg-sky-50/60 border-y border-sky-100">
        <div className="mx-auto max-w-3xl px-4 py-12 text-center">
          <Reveal>
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">Built by caregivers, for caregivers</h2>
            <p className="mt-3 text-slate-600 text-lg">
              CountedCare started with our own families — the shoeboxes of receipts, the mileage nobody logged, the deductions nobody claimed. We are a small, transparent team in Los Angeles, and we are honest about what the product does and does not do yet.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Button
                variant="outline"
                className="hover-scale transition-all duration-200 focus:ring-2 focus:ring-sky-600 focus:ring-offset-2"
                onClick={() => navigate("/about")}
              >
                Meet the team
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-7xl px-4 py-12">
        <Reveal as="h2" className="text-2xl md:text-3xl font-semibold text-slate-900">FAQ</Reveal>
        <Reveal delay={100} className="mt-6 max-w-3xl">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-left">Is CountedCare a tax advisor?</AccordionTrigger>
              <AccordionContent className="text-slate-600">
                No. We provide tools and education aligned to IRS Publication 502. Please consult a tax professional for personalized advice.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-left">Can I track mileage and receipts?</AccordionTrigger>
              <AccordionContent className="text-slate-600">
                Yes. Use the quick‑add modal to scan/upload receipts, enter mileage (with GPS or addresses), or add manual items.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-3">
              <AccordionTrigger className="text-left">Who is CountedCare for?</AccordionTrigger>
              <AccordionContent className="text-slate-600">
                Family caregivers supporting loved ones with medical, long‑term care, or special needs who want organized finances and potential tax savings. Also ideal for anyone with high out-of-pocket medical and dental expenses.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-4">
              <AccordionTrigger className="text-left">What is Publication 502?</AccordionTrigger>
              <AccordionContent className="text-slate-600">
                IRS Publication 502 is the official guide that defines which medical and dental expenses qualify for tax deductions. CountedCare automatically maps your expenses to these categories to help you identify potentially deductible costs.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-5">
              <AccordionTrigger className="text-left">Do you have a mobile app?</AccordionTrigger>
              <AccordionContent className="text-slate-600">
                Our web app is currently in beta. A dedicated mobile app is in development and not yet available.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-6">
              <AccordionTrigger className="text-left">Am I guaranteed to save money?</AccordionTrigger>
              <AccordionContent className="text-slate-600">
                No. Qualifying medical expenses must exceed 7.5% of your Adjusted Gross Income (AGI) to be deductible. AGI includes both earned income (wages, salary) and unearned income (investments, dividends). However, keeping organized records ensures you don't miss potential deductions and provides audit-ready documentation if needed.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Reveal>
      </section>

      {/* FOOTER */}
      <Footer />

      {/* Scroll to top button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-40 bg-sky-600 hover:bg-sky-700 text-white p-3 rounded-full shadow-lg hover-scale transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-sky-600 focus:ring-offset-2"
          aria-label="Scroll to top"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
